import React, { useState, useRef, useEffect } from 'react';
import { Layout, Button, Input, Loading, MessageBox, Notification, Dialog, Checkbox, Tooltip } from 'element-react';
import { useHistory } from "react-router-dom";
import { Link as ScrollLink } from 'react-scroll'; 
import DeleteIcon from '../components/DeleteIcon'; 
import PagerQuery from '../utils/PagerQuery';

// --- IMPORTS AWS ---
import { Auth, API, graphqlOperation } from 'aws-amplify';
import { listCompanys } from '../graphql/queries';
import { updateCompany } from '../graphql/mutations';

import VerifiedBadge from '../components/VerifiedBadge';

// --- URL DEL BACKEND ---
const API_BASE_URL = 'https://files.bainub.com/api/v1'; 

// --- ICONOS SVG ---
const LinkedInTitleIcon = ({ size = 28, style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" style={style}>
    <path fill="#0a66c2" d="M2.25 0h19.5A2.25 2.25 0 0 1 24 2.25v19.5A2.25 2.25 0 0 1 21.75 24H2.25A2.25 2.25 0 0 1 0 21.75V2.25A2.25 2.25 0 0 1 2.25 0z"/>
    <path fill="#ffffff" d="M6.3 9H9v8.4H6.3zM7.65 4.95a1.65 1.65 0 1 1-1.65 1.65 1.65 1.65 0 0 1 1.65-1.65zM10.8 9h2.55v1.2h.045a3 3 0 0 1 2.55-1.35c2.7 0 3.15 1.8 3.15 4.05V17.4h-2.7v-4.05c0-1.05-.45-1.8-1.35-1.8a1.45 1.45 0 0 0-1.35 1.05V17.4h-2.7V9z"/>
  </svg>
);

const LinkedInButtonIcon = ({ size = 20, style }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" style={style}>
    <path fill="#ffffff" d="M2.25 0h19.5A2.25 2.25 0 0 1 24 2.25v19.5A2.25 2.25 0 0 1 21.75 24H2.25A2.25 2.25 0 0 1 0 21.75V2.25A2.25 2.25 0 0 1 2.25 0z"/>
    <path fill="#0a66c2" d="M6.3 9H9v8.4H6.3zM7.65 4.95a1.65 1.65 0 1 1-1.65 1.65 1.65 1.65 0 0 1 1.65-1.65zM10.8 9h2.55v1.2h.045a3 3 0 0 1 2.55-1.35c2.7 0 3.15 1.8 3.15 4.05V17.4h-2.7v-4.05c0-1.05-.45-1.8-1.35-1.8a1.45 1.45 0 0 0-1.35 1.05V17.4h-2.7V9z"/>
  </svg>
);

// --- CONFIGURACIÓN DE DOCUMENTOS ---
const DOCS_MX = [
    { id: 1, label: 'Acta Constitutiva', fileName: null, status: 'PENDING', key: 'mx_acta' },
    { id: 2, label: 'INE de usuario', fileName: null, status: 'PENDING', key: 'mx_ine' },
    { id: 3, label: 'Comprobante de Domicilio', fileName: null, status: 'PENDING', key: 'mx_domicilio' },
    { id: 4, label: 'Constancia de Situación Fiscal', fileName: null, status: 'PENDING', key: 'mx_csf' },
];

const DOCS_US_CA = [
    { id: 1, label: 'W-9 Form or EIN Letter', fileName: null, status: 'PENDING', key: 'us_w9' },
    { id: 2, label: 'Government-issued ID', fileName: null, status: 'PENDING', key: 'us_gov_id' },
    { id: 3, label: 'Utility Bill or Business License displaying Business Address', fileName: null, status: 'PENDING', key: 'us_utility' },
];

export default function CompanyVerification() {
    const history = useHistory();
    const [loading, setLoading] = useState(true); 
    const fileInputRef = useRef(null);
    const [currentDocId, setCurrentDocId] = useState(null);

    const [companyData, setCompanyData] = useState(null); 
    const [companyName, setCompanyName] = useState("");
    const [isVerified, setIsVerified] = useState(false);
    
    // Estado de documentos
    const [documents, setDocuments] = useState(DOCS_MX);

    // Estado para el botón "Verify My Company"
    const [verifyAttempted, setVerifyAttempted] = useState(false); // Para mostrar bordes rojos
    const [verifySubmitted, setVerifySubmitted] = useState(false); // Para ocultar el botón después de presionarlo

    // --- ESTADOS PARA EL MODAL DE CONSENTIMIENTO ---
    const [consentModalVisible, setConsentModalVisible] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);
    const [pendingFile, setPendingFile] = useState(null); // Guarda el archivo hasta que el usuario acepte
    const [hasConsented, setHasConsented] = useState(false);

    // --- HELPER ---
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = error => reject(error);
        });
    }

    // --- CARGA INICIAL ---
    useEffect(() => {
        fetchCompanyData();
    }, []);

    const fetchCompanyData = async () => {
        console.log('Fetching company data...');
        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const user = await Auth.currentAuthenticatedUser();
            
            const pagerQueryOrders = new PagerQuery(listCompanys, { filter: { owner: { eq: user.username } } }, Object.keys({ listCompanys }));
            const result = await pagerQueryOrders.searchPrivateData();

            const items = result.data.listCompanys.items;

            if (items.length > 0) {
                const company = items[0];
                
                // RESTRICCIÓN: Si la empresa no está publicada, no puede verificar
                if (company.published !== 1) {
                    Notification({
                        title: 'Warning',
                        message: 'You must publish your Microsite before verifying your company.',
                        type: 'warning',
                        duration: 5000
                    });
                    history.push('/dashboard');
                    return;
                }

                setCompanyData(company); 
                
                setCompanyName(company.name || company.company_name || "My Company");
                
                const country = company.address?.country;
                // Definimos isInternational y la lista base
                const isInternational = ['US', 'USA', 'CA', 'CAN', 'Canada', 'United States'].includes(country);
                const baseDocs = isInternational ? JSON.parse(JSON.stringify(DOCS_US_CA)) : JSON.parse(JSON.stringify(DOCS_MX));
                
                // Actualizamos el estado documents INMEDIATAMENTE con la lista base correcta
                // Esto previene que se muestre la lista incorrecta si la API de verificación falla
                setDocuments(baseDocs);

                // C. CONSULTAR ESTADO DE ARCHIVOS SUBIDOS
                try {
                    const response = await fetch(`${API_BASE_URL}/company-verification?company_id=${company.id}`, {
                        headers: { 'Authorization': token }
                    });
                    
                    const apiData = await response.json();
                    
                    if (response.ok && apiData.success && apiData.data.length > 0) {
                        
                        // "Aplanamos" todos los archivos de todos los registros para encontrar todo lo subido
                        const allUploadedFiles = apiData.data.flatMap(record => record.files || []);

                        // Si ya hay archivos subidos, asumimos que ya consintió antes 
                        /*if (allUploadedFiles.length > 0) {
                           setHasConsented(true);
                        }*/
                        
                        // Verificamos estatus general
                        const isCompanyVerifiedAnywhere = apiData.data.some(record => record.company_verified === true);
                        if (isCompanyVerifiedAnywhere) {
                            setIsVerified(true);
                            // Sincronizar el campo 'verified' en DynamoDB/GraphQL
                            // para que visitantes sin sesión también puedan ver el badge
                            if (!company.verified) {
                                try {
                                    await API.graphql(graphqlOperation(updateCompany, {
                                        input: { id: company.id, verified: 1 }
                                    }));
                                    console.log('Company verified field synced to GraphQL');
                                } catch (syncErr) {
                                    console.warn('Could not sync verified field to GraphQL:', syncErr);
                                }
                            }
                        }

                        const updatedDocs = baseDocs.map(doc => {
                            // Buscamos archivo insensible a mayúsculas
                            const foundFile = allUploadedFiles
                                .filter(f => f.file_name.toLowerCase().startsWith(doc.key.toLowerCase()))
                                .sort((a, b) => new Date(b.file_created_date) - new Date(a.file_created_date))[0];
                            
                            if (foundFile) {
                                return {
                                    ...doc,
                                    fileName: foundFile.file_name,
                                    status: 'UPLOADED',
                                    fileVerified: foundFile.file_verified
                                };
                            }
                            return doc;
                        });
                        setDocuments(updatedDocs);
                    } 
                    // Si no hay datos, se queda con baseDocs que ya establecimos arriba

                } catch (apiError) {
                    console.error("Error fetching verification API:", apiError);
                    // No hacemos nada, ya tenemos baseDocs seteado
                }

                // D. CONSULTAR ESTADO DE CONSENTIMIENTO DE PRIVACIDAD (GET)
                try {
                    const consentResponse = await fetch(`${API_BASE_URL}/company-privacy-policy?company_id=${company.id}`, {
                        headers: { 'Authorization': token }
                    });
                    
                    const consentData = await consentResponse.json();
                    
                    // Asumimos que si devuelve data, es porque ya hay un registro de aceptación
                    if (consentResponse.ok && consentData.success && consentData.data && consentData.data.length > 0) {
                        setHasConsented(true);
                    }
                } catch (consentError) {
                    console.error("Error fetching consent API:", consentError);
                }

            } else {
                console.log("No company found for this user.");
            }

        } catch (e) {
            console.error("Error fetching data:", e);
            Notification.error("Failed to load company information.");
        } finally {
            setLoading(false);
        }
    };

    const triggerFileSelect = (docId) => {
        setCurrentDocId(docId);
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; 
            fileInputRef.current.click();
        }
    };

    // --- FUNCIÓN DE SUBIDA ---
    const performUpload = async (fileToUpload, docId) => {
        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const base64Data = await fileToBase64(fileToUpload);
            
            const currentDocInfo = documents.find(d => d.id === docId);
            const fileExtension = fileToUpload.name.split('.').pop();
            const storageName = `${currentDocInfo.key}.${fileExtension}`; 

            const isInternational = ['US', 'USA', 'CA', 'CAN', 'Canada', 'United States'].includes(companyData.address?.country);
            const typeToSend = isInternational ? "US" : "MX";

            const payload = {
                name: companyData.name || companyData.company_name,
                type: typeToSend,
                company_id: companyData.id,
                files: [{ name: storageName, file_data: base64Data }]
            };

            const response = await fetch(`${API_BASE_URL}/company-verification`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            const responseData = await response.json();

            if (response.ok && responseData.success) {
                const newDocs = documents.map(doc => {
                    if (doc.id === docId) {
                        return { ...doc, fileName: storageName, status: 'UPLOADED' };
                    }
                    return doc;
                });
                setDocuments(newDocs);
                setHasConsented(true); // <--- GUARDAMOS QUE YA CONSINTIÓ PARA LOS SIGUIENTES ARCHIVOS
                Notification.success("File uploaded successfully");
            } else {
                console.error("Backend Error Response:", responseData);
                throw new Error(responseData.error || responseData.message || "Unknown error from server");
            }

        } catch (error) {
            console.error("Upload error full details:", error);
            Notification.error("Upload Failed: " + error.message);
        } finally {
            setLoading(false);
            setCurrentDocId(null);
            setPendingFile(null);
        }
    };

    // --- CAPTURAR ARCHIVO Y MOSTRAR MODAL ---
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file || !currentDocId || !companyData) return;

        if (file.size > 11 * 1024 * 1024) {
            Notification.error("File is too large. Max size is 11MB.");
            e.target.value = ""; 
            setCurrentDocId(null);
            return;
        }

        if (hasConsented) {
            // SI YA CONSINTIÓ PREVIAMENTE: Omitimos el modal y subimos directo
            performUpload(file, currentDocId);
        } else {
            // SI ES LA PRIMERA VEZ: Guardamos temporalmente y mostramos el modal
            setPendingFile(file);
            setConsentChecked(false);
            setConsentModalVisible(true);
        }
        
        e.target.value = ""; 
    };

    // --- FUNCIÓN PARA CANCELAR ---
    const cancelUpload = () => {
        setConsentModalVisible(false);
        setCurrentDocId(null);
        setPendingFile(null);
    };

    // SUBIDA REAL (Se ejecuta al dar click en Confirmar en el Modal) ---
    const executeUpload = async () => {
        setConsentModalVisible(false);
        setLoading(true);

        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            // 1. ENVIAR EL LOG DE CONSENTIMIENTO (POST) - No bloqueante
            // Solo si no se ha registrado previamente (evita duplicate key en BD)
            if (!hasConsented) {
                try {
                    // Detectar idioma actual: "es" o "en"
                    const currentLang = (navigator.language || 'en').startsWith('es') ? 'es' : 'en';
                    
                    const consentPayload = {
                        company_id: companyData.id,
                        consent_type: "N",
                        privacy_policy_version: "v1.0",
                        tos_version: "v1.0",
                        policy_language: currentLang
                    };

                    const consentResponse = await fetch(`${API_BASE_URL}/company-privacy-policy`, {
                        method: 'POST',
                        headers: {
                            'Authorization': token,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(consentPayload)
                    });

                    if (!consentResponse.ok) {
                        const errorBody = await consentResponse.text();
                        console.warn("Consent API returned status:", consentResponse.status, "Body:", errorBody);
                        // Si es duplicate key, significa que ya existía - lo marcamos como consentido
                        if (errorBody.includes('duplicate key')) {
                            setHasConsented(true);
                        }
                    } else {
                        console.log("Consent registered successfully.");
                        setHasConsented(true);
                    }
                } catch (consentErr) {
                    console.warn("Consent registration failed, proceeding with upload:", consentErr.message);
                }
            }

            // 2. ACTUALIZAMOS ESTADO Y SUBIMOS ARCHIVO (independiente del resultado del consent)
            setHasConsented(true);
            await performUpload(pendingFile, currentDocId);

        } catch (error) {
            console.error("Upload error:", error);
            Notification.error("Error: " + error.message);
            setLoading(false);
            setCurrentDocId(null);
            setPendingFile(null);
        }
    };

    const handleDelete = (docId) => {
        MessageBox.confirm('Are you sure you want to delete this file?', 'Confirmation', { type: 'warning' })
            .then(() => {
                const newDocs = documents.map(doc => {
                    if (doc.id === docId) return { ...doc, fileName: null, status: 'PENDING' };
                    return doc;
                });
                setDocuments(newDocs);
                setVerifySubmitted(false);
            }).catch(() => {});
    };

    if (loading) return <Loading fullscreen={true} text="Processing..." />;

    return (
        <div className="verification-container">
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />

            {/* --- MODAL DE CONSENTIMIENTO --- */}
            <Dialog
                title="Privacy Consent"
                size="small"
                visible={consentModalVisible}
                onCancel={cancelUpload}
            >
                <Dialog.Body>
                    <Checkbox checked={consentChecked} onChange={setConsentChecked}>
                        <span style={{ 
                            display: 'inline-block', 
                            whiteSpace: 'normal', 
                            verticalAlign: 'top', 
                            lineHeight: '1.5', 
                            paddingLeft: '5px',
                            paddingRight: '5px',
                            color: '#333' 
                        }}>
                            I hereby provide my express consent for Bainub to process the documentation I submit exclusively for the purpose of company verification, in accordance with the Privacy Policy.
                        </span>
                    </Checkbox>
                </Dialog.Body>
                <Dialog.Footer className="dialog-footer">
                    <Button type='primary' onClick={cancelUpload}>Cancel</Button>
                    {/* El botón de confirmar estará deshabilitado hasta que se marque el checkbox */}
                    <Button type="primary" disabled={!consentChecked} onClick={executeUpload}>Confirm & Upload</Button>
                </Dialog.Footer>
            </Dialog>

            <div className="page-header">
                <div className="header-content">
                    <h1>
                        <span className="title-red">COMPANY </span>
                        <span className="title-dark">Verification Assistant</span>
                    </h1>
                    <p>
                        Bainub will provide a <strong style={{ color: '#F76566' }}>Verified Company</strong> badge <VerifiedBadge isVerified={true} style={{ marginLeft: '2px', marginRight: '2px' }} /> to your Company profile, visible for every potential buyer, once the requested documentation is provided.
                        <br />Learn more about company verification{' '}
                        <ScrollLink 
                            to="about-company-verification" 
                            smooth={true} 
                            duration={800} 
                            offset={-50} 
                            style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            here
                        </ScrollLink>
                        .
                    </p>
                </div>
            </div>

            <div className="verification-section">
                <h2>1. &nbsp;&nbsp; COMPANY VERIFICATION</h2>
                
                <div className="detail-card">
                    <div className="company-header-row">
                        <span className="label">COMPANY NAME:</span>
                        <span className="value" style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {companyName}
                            <VerifiedBadge isVerified={isVerified} />
                        </span>
                        {!isVerified && (
                            <span className="badge-not-verified">
                                <i className="el-icon-circle-close"></i> Not Verified
                            </span>
                        )}
                    </div>

                    <div className="documents-list-area">
                        <p className="instruction-text">Please upload the {documents.length} documents and click below to start the verification process:</p>

                        {documents.map((doc) => (
                            <Layout.Row key={doc.id} gutter="20" className="document-row">
                                <Layout.Col xs={24} sm={8} md={6}>
                                    <span className="doc-label">{doc.id}. {doc.label}</span>
                                </Layout.Col>

                                {/* COLUMNA CENTRAL (Input/Nombre/Eliminar) */}
                                <Layout.Col xs={20} sm={12} md={14}>
                                    {isVerified ? (
                                        /* SI ESTÁ VERIFICADO: No mostramos input ni nombre ni botón de borrar */
                                        <div className="doc-file-info" style={{ visibility: 'hidden' }}>
                                            {/* Dejamos el espacio vacío para mantener la alineación de las columnas */}
                                            <span>verified</span> 
                                        </div>
                                    ) : doc.status === 'UPLOADED' ? (
                                        /* SI ESTÁ SUBIDO PERO NO VERIFICADO: Mostramos nombre y botón borrar */
                                        <div className="doc-file-info">
                                            <span>{doc.fileName}</span>
                                            <div style={{ cursor: 'pointer', marginLeft: '10px', marginTop: '15px' }}>
                                                <DeleteIcon handleClick={() => handleDelete(doc.id)} width="35px" />
                                            </div>
                                        </div>
                                    ) : (
                                        /* SI NO HAY ARCHIVO: Mostramos el input y botón Browse */
                                        <div className={`doc-actions ${verifyAttempted ? 'doc-actions-error' : ''}`}>
                                            <Input placeholder="No file selected" disabled style={{ width: '100%', backgroundColor: '#fff' }} />
                                            <Button className="btn-browse" onClick={() => triggerFileSelect(doc.id)}>
                                                Browse
                                            </Button>
                                        </div>
                                    )}
                                </Layout.Col>

                                {/* COLUMNA DERECHA (Status) */}
                                <Layout.Col xs={4} sm={4} md={4} className="status-col">
                                    {isVerified ? (
                                         /* SI ESTÁ VERIFICADO: Check verde siempre */
                                        <i className="el-icon-check icon-check"></i>
                                    ) : doc.status === 'UPLOADED' ? (
                                         /* SUBIDO: Check verde temporal */
                                        <i className="el-icon-check icon-check"></i>
                                    ) : (
                                         /* PENDIENTE */
                                        <span className={`status-pending ${verifyAttempted ? 'status-pending-error' : ''}`}>PENDING</span>
                                    )}
                                </Layout.Col>
                            </Layout.Row>
                        ))}

                        {/* --- Botón VERIFY MY COMPANY --- */}
                        {!isVerified && !verifySubmitted && (() => {
                            const allUploaded = documents.every(doc => doc.status === 'UPLOADED');
                            const verifyButton = (
                                <Button
                                    className={`btn-verify-company ${allUploaded ? 'btn-verify-enabled' : 'btn-verify-disabled'}`}
                                    onClick={() => {
                                        if (allUploaded) {
                                            setVerifySubmitted(true);
                                            setVerifyAttempted(false);
                                        } else {
                                            setVerifyAttempted(true);
                                        }
                                    }}
                                >
                                    VERIFY MY COMPANY
                                </Button>
                            );
                            return (
                                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                                    {!allUploaded && verifyAttempted ? (
                                        <Tooltip content="Upload all files to continue" placement="top" effect="dark">
                                            {verifyButton}
                                        </Tooltip>
                                    ) : (
                                        verifyButton
                                    )}
                                </div>
                            );
                        })()}

                        <div className="help-text">
                            Need assistance with the verification process? We are here for you, send us a message: <a href="/help-support" target="_blank">Help & Support</a>
                        </div>
                    </div>
                </div>
            </div>

            {/* <div className="verification-section">
                <h2>2. &nbsp;&nbsp; ACCOUNT VERIFICATION</h2>
                <div className="linkedin-container">
                    <div className="linkedin-header">
                        <LinkedInTitleIcon size={32} style={{ marginRight: '8px' }} />
                        <h3><span>LINKEDIN</span> Verification</h3>
                    </div>
                    <p className="linkedin-desc">
                        Add a verification badge to your profile to signal authenticity and build trust, giving others more confidence to connect with you.
                    </p>
                    <Button className="btn-linkedin">
                        <LinkedInButtonIcon size={20} />
                        Verify on LinkedIn
                    </Button>
                    <div style={{ marginTop: '15px' }}>
                         <span style={{ color: '#888', fontSize: '13px' }}>Learn more about <a href="https://www.linkedin.com/help/linkedin/answer/a1359065" target="_blank" style={{ color: '#007bff' }}>Verified on LinkedIn</a></span>
                    </div>
                </div>
            </div> */}

            <div className="about-verification" id='about-company-verification'>
                <h3>ABOUT COMPANY VERIFICATION</h3>
                <Layout.Row className="qa-row">
                    <Layout.Col xs={24} sm={6} md={5}><h4>What is Company Verification?</h4></Layout.Col>
                    <Layout.Col xs={24} sm={18} md={19}><p>Company Verification is an essential step that validates you as an authorized representative of your business on Bainub. Verified companies gain greater credibility, stand out to corporate buyers seeking dependable suppliers, and build stronger commercial opportunities across North America.</p></Layout.Col>
                </Layout.Row>
                <Layout.Row className="qa-row">
                    <Layout.Col xs={24} sm={6} md={5}><h4>How it works</h4></Layout.Col>
                    <Layout.Col xs={24} sm={18} md={19}><p>Bainub will provide a <strong style={{ color: '#F76566' }}>Verified Company</strong> badge <VerifiedBadge isVerified={true} style={{ marginLeft: '2px', marginRight: '2px' }} /> to your Company profile, visible for every potential buyer, once the requested documentation is provided.</p></Layout.Col>
                </Layout.Row>
                <Layout.Row className="qa-row">
                    <Layout.Col xs={24} sm={6} md={5}><h4>Is it safe?</h4></Layout.Col>
                    <Layout.Col xs={24} sm={18} md={19}><p>Absolutely. The information we request is not sensitive.<br/>It is the same basic business information you would normally provide when applying to become a supplier of any company.<br/>We do not share your information with anyone; it is used solely to verify your company profile.</p></Layout.Col>
                </Layout.Row>
            </div>
        </div>
    );
}